import React from "react";
import {
  Box,
  Button,
  HStack,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { FaRegTrashAlt } from "react-icons/fa";
const FONT_FAMILY = "'Nunito','Inter', sans-serif";

export interface DeleteOptionControlProps {
  isDisabled: boolean;
  disabledTooltip?: string;
  isDeleting: boolean;
  onConfirmDelete: () => Promise<void>;
}

export const DeleteOptionControl: React.FC<DeleteOptionControlProps> = ({
  isDisabled,
  disabledTooltip,
  isDeleting,
  onConfirmDelete,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleDelete = async () => {
    await onConfirmDelete();
    onClose();
  };

  const triggerButton = (
    <IconButton
      aria-label="Delete delivery option"
      icon={<FaRegTrashAlt size={14} />}
      size="xs"
      variant="ghost"
      colorScheme="red"
      minW="20px"
      w="20px"
      h="20px"
      p={0}
      _hover={{ color: "red.500", bg: "transparent" }}
      isDisabled={isDisabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!isDisabled) {
          onOpen();
        }
      }}
    />
  );

  const popover = (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      placement="left"
      closeOnBlur
      isLazy
    >
      <PopoverTrigger>
        <Box display="inline-flex" lineHeight={0}>
          {triggerButton}
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          zIndex={1600}
          width="200px"
          boxShadow="lg"
          onClick={(e) => e.stopPropagation()}
        >
          <PopoverBody>
            <VStack spacing={2} align="stretch" mt={1}>
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="#4A5568"
                  mb={1}
                  fontFamily={FONT_FAMILY}
                >
                  Delete Option?
                </Text>
                <Text
                  fontSize="xs"
                  color="gray.500"
                  fontFamily={FONT_FAMILY}
                  fontWeight="600"
                  lineHeight="short"
                >
                  This action cannot be undone.
                </Text>
              </Box>
              <HStack spacing={2} pt={2}>
                <Button
                  size="xs"
                  variant="ghost"
                  flex="1"
                  onClick={onClose}
                  fontFamily={FONT_FAMILY}
                  fontWeight="700"
                  color="#2D3748"
                >
                  Cancel
                </Button>
                <Button
                  size="xs"
                  colorScheme="red"
                  flex="1"
                  onClick={handleDelete}
                  isLoading={isDeleting}
                  fontFamily={FONT_FAMILY}
                  fontWeight="700"
                >
                  Delete
                </Button>
              </HStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );

  if (isDisabled && disabledTooltip) {
    return (
      <Tooltip
        label={disabledTooltip}
        hasArrow
        placement="top"
        shouldWrapChildren
      >
        <Box display="inline-flex" lineHeight={0}>
          {triggerButton}
        </Box>
      </Tooltip>
    );
  }

  return popover;
};
